import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CreditCard,
  X
} from 'lucide-react';
import Images from '../../Images';
import ProcessingModal from '../../components/ProcessingModal';
import Navbar from '../../components/Navbar';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNetwork, setMobileNetwork] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [isProcessingOpen, setIsProcessingOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('mobile');
  const [numberError, setNumberError] = useState('');
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, []);
  
  // Get package details from location state
  const packageDetails = location.state?.packageDetails;

  const mobileNetworks = [
    {
      id: 'mtn',
      name: 'MTN Mobile Money',
      logo: Images.mtn_momo,
      color: '#FDB913',
      code:'MTN'
    },
    {
      id: 'airtel-tigo',
      name: 'AirtelTigo Money',
      logo: Images.at_cash,
      color: '#E40414',
      code:'TGO'
    },
    {
      id: 'telecel',
      name: 'Telecel Cash',
      logo: Images.telecel_cash,
      color: '#0066B3',
      code:'VDF'
    }
  ];

  

  const validateMobileNumber = (number) => {
    const cleanNumber = number.replace(/\D/g, '');    
    if (cleanNumber.length !== 10) {
      return {
        isValid: false,
        error: 'Number must be 10 digits'
      };
    }
    return {
      isValid: true,
      error: ''
    };
  };

  // Update mobile number with validation
  const handleMobileNumberChange = (e) => {
    const newNumber = e.target.value;
    setMobileNumber(newNumber);
    
    if (newNumber) {
      const validationResult = validateMobileNumber(newNumber);
      setNumberError(validationResult.error);
      setIsValid(validationResult.isValid);
    } else {
      setNumberError('');
      setIsValid(false);
    }
  };

  // Handle network change
  const handleNetworkChange = (network) => {
    setMobileNetwork(network);
  };

  // Form validation
  const isFormValid = () => {
    return mobileNetwork && mobileNumber.length === 10 && isValid;
  };


  const handlePayment = () => {
    setIsProcessingOpen(true);
  };
  const payload = {
    phone_number: mobileNumber,
    network: mobileNetwork,
    subscription_plan: packageDetails.id,
  }


  const handleBack = () => {
    navigate(-1);
  };

  if (!packageDetails) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Invalid Package Details</h2>
          <p className="mt-2 text-gray-600">Please select a package from the pricing page.</p>
          <button
            onClick={handleBack}
            className="mt-4 px-6 py-2 text-white bg-[#FF6C6C] rounded-lg hover:bg-[#ff5555]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Enhanced Header Section */}
        <div className="relative overflow-hidden bg-gradient-to-b from-[#fff1f1] via-[#fff5f5] to-transparent">
          <div className="absolute inset-0">
            <div className="absolute -left-4 top-0 w-72 h-72 bg-[#FF6C6C] rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
            <div className="absolute -right-4 top-0 w-72 h-72 bg-[#FF8F8F] rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-[#FFB4B4] rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
          </div>
          
          <div className="relative px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col justify-center items-center"
              >
                <span className="px-4 py-1.5 mb-4 text-sm font-semibold text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-full">
                  Payment Details
                </span>
                <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl lg:text-6xl">
                  Complete Your{' '}
                  <span className="bg-gradient-to-r from-[#FF6C6C] to-[#FF9494] bg-clip-text text-transparent">
                    Purchase
                  </span>
                </h1>
                <p className="mx-auto max-w-2xl text-lg text-gray-600 md:text-xl">
                  You're just a few steps away from accessing{' '}
                  <span className="font-semibold text-gray-800">{packageDetails.name}</span>
                </p>
              </motion.div>

              {/* Payment Progress Indicator */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex justify-center items-center mt-8 space-x-4"
              >
                {['Package Selection', 'Payment Details', 'Confirmation'].map((step, index) => (
                  <React.Fragment key={step}>
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        index === 1 
                          ? 'bg-[#FF6C6C] text-white' 
                          : index < 1 
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-400'
                      }`}>
                        {index < 1 ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <span className="text-sm">{index + 1}</span>
                        )}
                      </div>
                      <span className={`ml-2 text-sm ${
                        index === 1 
                          ? 'text-gray-900 font-medium' 
                          : 'text-gray-500'
                      }`}>
                        {step}
                      </span>
                    </div>
                    {index < 2 && (
                      <div className={`w-12 h-0.5 ${
                        index < 1 ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </motion.div>
            </div>
          </div>
        </div>

        <div className="px-4 py-12 mx-auto max-w-7xl">
          {/* Package Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12 p-6 md:p-10 bg-white rounded-[2.5rem] shadow-xl border border-gray-100"
          >
            <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center">
              <h2 className="text-2xl font-bold md:text-3xl">
                <span className="bg-gradient-to-r from-[#FF6C6C] to-[#FF9494] bg-clip-text text-transparent">
                  Package Features
                </span>
              </h2>
              <div className="inline-flex px-4 py-1.5 bg-gradient-to-r from-[#FF6C6C] to-[#FF9494] rounded-full text-white text-sm font-medium">
                {packageDetails.name}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
              {Object.entries(packageDetails.features)
                .filter(([key]) => key !== 'analytics_access')
                .map(([key, value]) => (
                  <div 
                    key={key} 
                    className="flex items-center p-4 space-x-4 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 md:p-6 hover:shadow-md"
                  >
                    <div className="p-2.5 md:p-3 bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF9494]/10 rounded-xl">
                      <Check className="w-5 h-5 md:w-6 md:h-6 text-[#FF6C6C]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 md:text-base">
                      {key === 'sms_credits' ? `${value} SMS Credits` :
                       key === 'max_offers' ? `${value} Offers` :
                       key === 'max_deals' ? `${value} Deals` :
                       key === 'max_leads' ? `${value} Leads` :
                       key === 'max_sender_ids' ? `${value} Sender ID${value !== 1 ? 's' : ''}` :
                       key === 'max_promotions' ? `${value} Promotion${value !== 1 ? 's' : ''}` :
                       key === 'number_of_users' ? `${value} User${value !== 1 ? 's' : ''}` :
                       value}
                    </span>
                  </div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Payment Method Selection */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-10 bg-white rounded-[2.5rem] shadow-xl border border-gray-100"
            >
              <h2 className="mb-8 text-3xl font-bold text-gray-900">
                Payment Method
              </h2>
              <div className="space-y-4">
                {mobileNetworks.map((network) => (
                  <button
                    key={network.id}
                    onClick={() => setMobileNetwork(network.code)}
                    className={`w-full flex items-center p-6 space-x-4 rounded-2xl transition-all duration-300 ${
                      mobileNetwork === network.code
                        ? 'bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9494]/10 border-2 border-[#FF6C6C]'
                        : 'bg-gray-50 border-2 border-gray-100 hover:border-[#FF6C6C]/50'
                    }`}
                  >
                    <img
                      src={network.logo}
                      alt={network.code}
                      className="object-contain w-12 h-12 rounded-lg"
                    />
                    <div className="flex-1 text-left">
                      <h3 className="font-semibold text-gray-900">{network.name}</h3>
                      <p className="text-sm text-gray-500">Pay with mobile money</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 ${
                      mobileNetwork === network.code
                        ? 'border-[#FF6C6C] bg-[#FF6C6C]'
                        : 'border-gray-300'
                    }`}>
                      {mobileNetwork === network.code && (
                        <Check className="w-5 h-5 text-white" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-10 bg-white rounded-[2.5rem] shadow-xl border border-gray-100"
            >
              <h2 className="mb-8 text-3xl font-bold text-gray-900">
                Order Summary
              </h2>
              <div className="space-y-6">
                <div className="flex justify-between items-center p-6 bg-gray-50 rounded-2xl">
                  <span className="text-gray-600">Package</span>
                  <span className="font-semibold text-gray-900">{packageDetails.name}</span>
                </div>
                <div className="flex justify-between items-center p-6 bg-gray-50 rounded-2xl">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-semibold text-gray-900">{packageDetails.duration_days} days</span>
                </div>
                <div className="flex justify-between items-center p-8 bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9494]/10 rounded-2xl">
                  <span className="text-xl font-semibold text-gray-900">Total Amount</span>
                  <span className="text-3xl font-bold text-[#FF6C6C]">
                    GH₵{parseFloat(packageDetails.price).toFixed(2)}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Payment Details */}
          {paymentMethod === 'mobile' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 p-10 bg-white rounded-[2.5rem] shadow-xl border border-gray-100"
            >
              <div className="mx-auto space-y-6 max-w-md">
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={handleMobileNumberChange}
                      placeholder="Enter your mobile number"
                      maxLength={10}
                      className={`w-full px-6 py-4 text-lg border-2 rounded-2xl focus:outline-none transition-all duration-300 ${
                        numberError
                          ? 'border-red-500 bg-red-50'
                          : isValid
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 focus:border-[#FF6C6C] focus:bg-[#FF6C6C]/5'
                      }`}
                    />
                    {numberError && (
                      <p className="flex items-center mt-2 text-sm text-red-500">
                        <X className="mr-1 w-4 h-4" />
                        {numberError}
                      </p>
                    )}
                    {isValid && (
                      <p className="flex items-center mt-2 text-sm text-green-500">
                        <Check className="mr-1 w-4 h-4" />
                        Valid number
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={!isFormValid()}
                  className={`
                    relative w-full py-6 rounded-2xl font-semibold text-center text-lg
                    transition-all duration-500 transform hover:scale-[1.02]
                    ${isFormValid()
                      ? 'bg-gradient-to-r from-[#FF6C6C] to-[#FF9494] text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  <span className="flex justify-center items-center">
                    {isFormValid() && <CreditCard className="mr-2 w-5 h-5" />}
                    Pay GH₵{parseFloat(packageDetails.price).toFixed(2)}
                  </span>
                </button>

                <button
                  onClick={handleBack}
                  className="w-full py-4 rounded-2xl font-semibold text-center transition-all duration-300 
                    border-2 border-gray-200 text-gray-600 hover:border-[#FF6C6C] hover:text-[#FF6C6C]
                    flex items-center justify-center"
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Go Back
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Processing Modal */}
      {isProcessingOpen && (
        <ProcessingModal
          paymentMethod={paymentMethod}
          onComplete={() => setIsProcessingOpen(false)}
          isProcessingOpen={isProcessingOpen}
          paymentDetails={packageDetails}
          processingSteps={[
            'Initiating payment',
            'Waiting for confirmation',
            'Processing payment',
            'Verifying transaction'
          ]}
          payload={payload}
        />
      )}
    </>
  );
};

export default PaymentPage; 